import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Download,
  Plus,
  Eye,
  Edit,
  CheckCircle,
  AlertCircle,
  Clock,
  IndianRupee,
  Users,
  Calendar,
  FileText,
  Settings,
  BarChart3,
  Zap,
  RefreshCw,
  X,
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { toast } from 'react-hot-toast';
import { payrollRunApi } from '../../../api/payrollRunApi';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('current');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExceptionsModal, setShowExceptionsModal] = useState(false);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [selectedException, setSelectedException] = useState(null);
  const [selectedPayGroup, setSelectedPayGroup] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Dropdown filter state
  const [filterRangeOption, setFilterRangeOption] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });

  const getDateRangeFromOption = useCallback((option, customRange) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (option) {
      case 'this_month': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = formatDate(start);
        endDate = formatDate(end);
        break;
      }
      case 'last_month': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = formatDate(start);
        endDate = formatDate(end);
        break;
      }
      case 'last_3_months': {
        const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        startDate = formatDate(start);
        endDate = formatDate(today);
        break;
      }
      case 'last_6_months': {
        const start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
        startDate = formatDate(start);
        endDate = formatDate(today);
        break;
      }
      case 'this_year': {
        const start = new Date(today.getFullYear(), 0, 1);
        const end = new Date(today.getFullYear(), 11, 31);
        startDate = formatDate(start);
        endDate = formatDate(end);
        break;
      }
      case 'last_year': {
        const start = new Date(today.getFullYear() - 1, 0, 1);
        const end = new Date(today.getFullYear() - 1, 11, 31);
        startDate = formatDate(start);
        endDate = formatDate(end);
        break;
      }
      case 'custom': {
        startDate = customRange.startDate;
        endDate = customRange.endDate;
        break;
      }
      default: // 'all'
        startDate = '';
        endDate = '';
        break;
    }

    return { startDate, endDate };
  }, []);

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };
  const [loading, setLoading] = useState(false);

  // Real data states
  const [dashboardData, setDashboardData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payrollStats, setPayrollStats] = useState({
    totalSalaryStructures: 0,
    totalPayrollRuns: 0,
    totalFinalSettlements: 0
  });

  // Quick stats data for each module
  const [salaryStructuresData, setSalaryStructuresData] = useState([]);
  const [payslipsData, setPayslipsData] = useState([]);
  const [settlementsData, setSettlementsData] = useState([]);
  const [quickStatsPayrollRuns, setQuickStatsPayrollRuns] = useState([]);
  const [loadingQuickStats, setLoadingQuickStats] = useState(false);

  const loadPayrollRuns = useCallback(async (startDate, endDate) => {
    try {
      const params = {
        page: 1,
        limit: 100,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };
      if (startDate && endDate) {
        params.runStartDate = startDate;
        params.runEndDate = endDate;
      }
      const response = await payrollRunApi.getPayrollRuns(params);
      if (response.responseCode === 200) {
        // Sort by createdAt descending (keep all for other uses)
        const sortedRuns = Array.isArray(response.data)
          ? response.data.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.runDate || 0);
            const dateB = new Date(b.createdAt || b.runDate || 0);
            return dateB - dateA;
          })
          : [];
        setPayrollRuns(sortedRuns);
      } else {
        toast.error('Failed to load payroll runs');
      }
    } catch (error) {

      toast.error('Error loading payroll runs');
    }
  }, []);

  const loadPayrollStats = useCallback(async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await payrollRunApi.getPayrollStats(params);


      // API response structure: { responseCode: 200, responseMessage: "...", data: { totalSalaryStructures, totalPayrollRuns, totalFinalSettlements } }
      let statsData = {};

      // Handle different possible response structures
      if (response?.data && typeof response.data === 'object' && 'totalSalaryStructures' in response.data) {
        // Direct data object
        statsData = response.data;
      } else if (response?.data?.data && typeof response.data.data === 'object') {
        // Nested data structure
        statsData = response.data.data;
      } else if (response && typeof response === 'object' && 'totalSalaryStructures' in response) {
        // Response itself is the data
        statsData = response;
      }



      if (statsData && (statsData.totalSalaryStructures !== undefined || statsData.totalPayrollRuns !== undefined || statsData.totalFinalSettlements !== undefined)) {
        setPayrollStats({
          totalSalaryStructures: statsData.totalSalaryStructures ?? 0,
          totalPayrollRuns: statsData.totalPayrollRuns ?? 0,
          totalFinalSettlements: statsData.totalFinalSettlements ?? 0
        });
        setDashboardData({
          kpis: {
            totalEmployees: statsData.kpis?.totalEmployees ?? 0,
            monthlyPayroll: statsData.kpis?.monthlyPayroll ?? 0,
            avgSalary: statsData.kpis?.avgSalary ?? 0
          }
        });

      } else {

      }
    } catch (error) {

      // Don't show toast error for stats, just log it
    }
  }, []);

  // Load dashboard data on component mount
  const loadQuickStats = useCallback(async (startDate, endDate) => {
    try {
      setLoadingQuickStats(true);

      const params = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await payrollRunApi.getQuickStats(params);

      // Handle different response structures
      let quickStatsData = {};
      if (response?.data && typeof response.data === 'object') {
        quickStatsData = response.data;
      } else if (response && typeof response === 'object' && 'salaryStructures' in response) {
        quickStatsData = response;
      }

      // Set data for each section
      setSalaryStructuresData(Array.isArray(quickStatsData.salaryStructures) ? quickStatsData.salaryStructures : []);
      setQuickStatsPayrollRuns(Array.isArray(quickStatsData.payrollRuns) ? quickStatsData.payrollRuns : []);
      setPayslipsData(Array.isArray(quickStatsData.payslipBatches) ? quickStatsData.payslipBatches : []);
      setSettlementsData(Array.isArray(quickStatsData.settlements) ? quickStatsData.settlements : []);
    } catch (error) {

    } finally {
      setLoadingQuickStats(false);
    }
  }, []);

  useEffect(() => {
    const { startDate, endDate } = getDateRangeFromOption(filterRangeOption, customDateRange);

    if (filterRangeOption !== 'custom' || (startDate && endDate)) {
      loadPayrollRuns(startDate, endDate);
      loadPayrollStats(startDate, endDate);
      loadQuickStats(startDate, endDate);
    }
  }, [filterRangeOption, customDateRange, loadPayrollRuns, loadPayrollStats, loadQuickStats, getDateRangeFromOption]);


  const getStatusColor = (status) => {
    switch (status) {
      case 'locked': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Navigation and action functions
  const handleExportReport = () => {
    try {
      const csvRows = [];

      // Add KPI Section
      csvRows.push('=== PAYROLL DASHBOARD KPI ===');
      csvRows.push('Metric,Value');
      if (dashboardData?.kpis) {
        csvRows.push(`Total Employees,${formatIndianNumber(dashboardData.kpis.totalEmployees || 0)}`);
        csvRows.push(`Monthly Payroll,${formatIndianAmount(dashboardData.kpis.monthlyPayroll || 0)}`);
        csvRows.push(`Avg Salary,${formatIndianAmount(dashboardData.kpis.avgSalary || 0)}`);
      }
      csvRows.push(`Compensation profiles (employee records),${formatIndianNumber(payrollStats.totalSalaryStructures || 0)}`);
      csvRows.push(`Total Payroll,${formatIndianNumber(payrollStats.totalPayrollRuns || 0)}`);
      csvRows.push(`Total Final Settlement,${formatIndianNumber(payrollStats.totalFinalSettlements || 0)}`);
      csvRows.push('');

      // Add recent compensation quick stats (employee-scoped snapshots)
      csvRows.push('=== RECENT COMPENSATION (EMPLOYEES) ===');
      csvRows.push('Name,Department,CTC/Net Pay,Status');
      if (salaryStructuresData.length > 0) {
        salaryStructuresData.forEach(structure => {
          const name = (structure.name || structure.employeeId || 'N/A').replace(/,/g, ';');
          const department = (structure.department || 'N/A').replace(/,/g, ';');
          const amount = formatIndianAmount(structure.ctc || structure.netPay || 0);
          const status = structure.status || 'N/A';
          csvRows.push(`${name},${department},${amount},${status}`);
        });
      } else {
        csvRows.push('No data available');
      }
      csvRows.push('');

      // Add Payroll Run Quick Stats
      csvRows.push('=== PAYROLL RUN QUICK STATS ===');
      csvRows.push('Pay Group Name,Net Pay');
      if (quickStatsPayrollRuns.length > 0) {
        quickStatsPayrollRuns.forEach(run => {
          const payGroupName = (run.payGroupName || run.payGroup || 'N/A').replace(/,/g, ';');
          const netPay = formatIndianAmount(run.netPay || 0);
          csvRows.push(`${payGroupName},${netPay}`);
        });
      } else {
        csvRows.push('No data available');
      }
      csvRows.push('');

      // Add Payslips Quick Stats
      csvRows.push('=== PAYSLIPS QUICK STATS ===');
      csvRows.push('Pay Group Name,Period,Total Net Pay,Total Employees');
      if (payslipsData.length > 0) {
        payslipsData.forEach(batch => {
          const payGroupName = (batch.payGroupName || 'N/A').replace(/,/g, ';');
          const period = (batch.period || 'N/A').replace(/,/g, ';');
          const totalNetPay = formatIndianAmount(batch.totalNetPay || 0);
          const totalEmployees = formatIndianNumber(batch.totalEmployees || 0);
          csvRows.push(`${payGroupName},${period},${totalNetPay},${totalEmployees}`);
        });
      } else {
        csvRows.push('No data available');
      }
      csvRows.push('');

      // Add Full & Final Settlement Quick Stats
      csvRows.push('=== FULL & FINAL SETTLEMENT QUICK STATS ===');
      csvRows.push('Employee Name,Department,Net Payable,Status');
      if (settlementsData.length > 0) {
        settlementsData.forEach(settlement => {
          const employeeName = (settlement.employeeName || 'N/A').replace(/,/g, ';');
          const department = (settlement.department || 'N/A').replace(/,/g, ';');
          const netPayable = formatIndianAmount(settlement.netPayable || settlement.totalAmount || 0);
          const status = settlement.status || 'N/A';
          csvRows.push(`${employeeName},${department},${netPayable},${status}`);
        });
      } else {
        csvRows.push('No data available');
      }

      // Create CSV content
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const { startDate, endDate } = getDateRangeFromOption(filterRangeOption, customDateRange);
      const rangeSuffix = startDate && endDate ? `_${startDate}_to_${endDate}` : '_all_time';
      link.setAttribute('download', `payroll_dashboard_export${rangeSuffix}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Payroll dashboard data exported successfully!');
    } catch (error) {

      toast.error('Failed to export data');
    }
  };


  const handleNewPayrollRun = () => {
    navigate('/payroll_run');
  };


  const handleUpdatePayGroup = async () => {
    try {
      setLoading(true);

      // Call API to update payroll run
      const response = await payrollRunApi.updatePayrollRun(selectedPayGroup.id, editFormData);

      if (response.responseCode === 200) {
        // Update local state
        setPayrollRuns(prev => prev.map(run =>
          run.id === selectedPayGroup.id ? { ...run, ...editFormData } : run
        ));

        setShowEditModal(false);
        setSelectedPayGroup(null);
        setEditFormData({});
        toast.success('Pay group updated successfully!');

        // Refresh dashboard data
        // loadDashboardData();
      } else {
        toast.error(response.responseMessage || 'Failed to update pay group');
      }
    } catch (error) {

      toast.error('Error updating pay group');
    } finally {
      setLoading(false);
    }
  };


  const handleCloseModals = () => {
    setShowViewModal(false);
    setShowEditModal(false);
    setShowExceptionsModal(false);
    setShowActivitiesModal(false);
    setSelectedPayGroup(null);
    setEditFormData({});
  };


  const handleViewException = (exceptionId) => {
    // Find the exception by ID
    const exception = exceptions.find(ex => ex.id === exceptionId);
    if (exception) {
      setSelectedException(exception);
      setShowExceptionModal(true);
    } else {
      toast.error('Exception not found');
    }
  };

  // Filtered pay groups
  const filteredPayGroups = payrollRuns.filter(run => {
    if (!run) return false;
    const matchesSearch = (run.payGroup || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (run.period || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || run.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
        <span className="ml-2">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage payroll operations and monitor compliance</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <PermissionGate permission="payroll" action="read">
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportReport}
            >
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="payroll" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleNewPayrollRun}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              New Payroll Run
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4 transition-all duration-300">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-sm font-semibold text-gray-700 flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-agrosaf-green" />
            Operation Period:
          </span>
          <select
            value={filterRangeOption}
            onChange={(e) => setFilterRangeOption(e.target.value)}
            className="block w-full sm:w-52 pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent cursor-pointer bg-white shadow-sm font-medium text-gray-800 transition-colors"
          >
            <option value="all">All Data</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="last_6_months">Last 6 Months</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Date</option>
          </select>
        </div>

        {filterRangeOption === 'custom' && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 animate-fadeIn">
            <span className="text-sm font-medium text-gray-600">Select Range:</span>
            <DateRangeFilter
              startDate={customDateRange.startDate}
              endDate={customDateRange.endDate}
              onChange={(newRange) => setCustomDateRange(newRange)}
              inputClassName="pl-3 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm w-full sm:w-36 cursor-pointer text-gray-800 bg-white"
            />
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {dashboardData && (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Users className="h-5 w-5 text-blue-500" />
                <span className="text-sm text-gray-600">Active Staff</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(dashboardData.kpis?.totalEmployees || 0)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center space-x-2 mb-2">
                <IndianRupee className="h-5 w-5 text-purple-500" />
                <span className="text-sm text-gray-600">Monthly Payroll</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatIndianAmount(dashboardData.kpis?.monthlyPayroll || 0)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="h-5 w-5 text-indigo-500" />
                <span className="text-sm text-gray-600">Avg Salary</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatIndianAmount(dashboardData.kpis?.avgSalary || 0)}</p>
            </div>
          </>
        )}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Calendar className="h-5 w-5 text-orange-500" />
            <span className="text-sm text-gray-600">Total Payroll</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(payrollStats?.totalPayrollRuns ?? 0)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm text-gray-600">Total Final Settlement</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(payrollStats?.totalFinalSettlements ?? 0)}</p>
        </div>
      </div>

      {/* Quick Stats Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Salary Structure Quick Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Settings className="h-5 w-5 mr-2 text-green-500" />
              Recent compensation
            </h3>
          </div>
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Employees with saved structure</span>
                <span className="text-2xl font-bold text-gray-900">{formatIndianNumber(payrollStats.totalSalaryStructures)}</span>
              </div>
            </div>
            <div className="space-y-3">
              {loadingQuickStats ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green mx-auto"></div>
                </div>
              ) : salaryStructuresData.length > 0 ? (
                salaryStructuresData.map((structure) => (
                  <div key={structure.id || structure._id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-words">{structure.name || structure.employeeId || 'N/A'}</p>
                        <p className="text-xs text-gray-500 break-words mt-1">{structure.department || 'N/A'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatIndianAmount(structure.ctc || structure.netPay || 0)}</p>
                        <span className={`inline-block text-xs px-2 py-1 rounded-full mt-1 capitalize ${structure.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {structure.status || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No recent compensation records</p>
              )}
            </div>
          </div>
        </div>

        {/* Payroll Run Quick Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Zap className="h-5 w-5 mr-2 text-orange-500" />
              Payroll Run
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/payroll_run')}
            >
              View All
            </Button>
          </div>
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Total Runs</span>
                <span className="text-2xl font-bold text-gray-900">{formatIndianNumber(payrollStats.totalPayrollRuns)}</span>
              </div>
            </div>
            <div className="space-y-3">
              {loadingQuickStats ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green mx-auto"></div>
                </div>
              ) : quickStatsPayrollRuns.length > 0 ? (
                quickStatsPayrollRuns.map((run) => (
                  <div key={run.id || run._id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-words">{run.payGroupName || 'N/A'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatIndianAmount(run.netPay || 0)}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No payroll runs found</p>
              )}
            </div>
          </div>
        </div>

        {/* Payslips Quick Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-500" />
              Payslips
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/payslips')}
            >
              View All
            </Button>
          </div>
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Total Batches</span>
                <span className="text-2xl font-bold text-gray-900">{formatIndianNumber(payslipsData.length)}</span>
              </div>
            </div>
            <div className="space-y-3">
              {loadingQuickStats ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green mx-auto"></div>
                </div>
              ) : payslipsData.length > 0 ? (
                payslipsData.map((batch) => (
                  <div key={batch.id || batch.payrollRunId} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-words">{batch.payGroupName || 'N/A'}</p>
                        <p className="text-xs text-gray-500 break-words mt-1">{batch.period || 'N/A'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatIndianAmount(batch.totalNetPay || 0)}</p>
                        <p className="text-xs text-gray-500 whitespace-nowrap mt-1">{formatIndianNumber(batch.totalEmployees || 0)} employees</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No payslip batches found</p>
              )}
            </div>
          </div>
        </div>

        {/* Full & Final Settlement Quick Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-red-500" />
              Full & Final Settlement
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/full_final')}
            >
              View All
            </Button>
          </div>
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Total Settlements</span>
                <span className="text-2xl font-bold text-gray-900">{formatIndianNumber(payrollStats.totalFinalSettlements)}</span>
              </div>
            </div>
            <div className="space-y-3">
              {loadingQuickStats ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green mx-auto"></div>
                </div>
              ) : settlementsData.length > 0 ? (
                settlementsData.map((settlement) => (
                  <div key={settlement._id || settlement.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-words">{settlement.employeeName || 'N/A'}</p>
                        <p className="text-xs text-gray-500 break-words mt-1">{settlement.department || 'N/A'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatIndianAmount(settlement.netPayable || settlement.totalAmount || 0)}</p>
                        <span className={`inline-block text-xs px-2 py-1 rounded-full mt-1 capitalize ${getStatusColor(settlement.status)}`}>
                          {settlement.status || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No settlements found</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Pay Group Modal */}
      {showViewModal && selectedPayGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Pay Group Details</h2>
                <button
                  onClick={handleCloseModals}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pay Group Name</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedPayGroup.payGroup}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedPayGroup.period}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedPayGroup.status)}`}>
                      {selectedPayGroup.status?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Headcount</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedPayGroup.headcount}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay</label>
                    <p className="text-lg font-semibold text-gray-900">{formatIndianAmount(selectedPayGroup.netPay || 0)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Run Date</label>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedPayGroup.runDate ? new Date(selectedPayGroup.runDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Processed By</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedPayGroup.processedBy || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <Button
                    variant="secondary"
                    onClick={handleCloseModals}
                  >
                    Close
                  </Button>
                  <PermissionGate permission="payroll" action="update">
                    <Button
                      variant="primary"
                      icon={<Edit className="h-4 w-4" />}
                      onClick={() => {
                        setShowViewModal(false);
                        setEditFormData({ ...selectedPayGroup });
                        setShowEditModal(true);
                      }}
                      className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    >
                      Edit Pay Group
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View All Exceptions Modal */}
      {showExceptionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">All Payroll Exceptions</h2>
                <button
                  onClick={handleCloseModals}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Severity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {exceptions.map((exception) => (
                      <tr key={exception.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{exception.employeeName}</div>
                            <div className="text-sm text-gray-500">{exception.employeeId}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{exception.type}</div>
                          <div className="text-sm text-gray-500">{exception.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(exception.severity)}`}>
                            {exception.severity}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${exception.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {exception.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {exception.assignedTo}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(exception.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewException(exception.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {exception.status === 'pending' && (
                              <button
                                className="text-green-600 hover:text-green-900"
                                title="Resolve Exception"
                                onClick={() => {
                                  setExceptions(prev => prev.map(ex =>
                                    ex.id === exception.id
                                      ? { ...ex, status: 'resolved', resolvedAt: new Date().toISOString().split('T')[0] }
                                      : ex
                                  ));
                                  toast.success('Exception resolved successfully!');
                                }}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <Button
                  variant="secondary"
                  onClick={handleCloseModals}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  icon={<Download className="h-4 w-4" />}
                  onClick={() => {
                    const csvContent = "data:text/csv;charset=utf-8,"
                      + "ID,Employee ID,Employee Name,Type,Severity,Status,Assigned To,Created Date\n"
                      + exceptions.map(ex =>
                        `${ex.id},${ex.employeeId},${ex.employeeName},${ex.type},${ex.severity},${ex.status},${ex.assignedTo},${ex.createdAt}`
                      ).join("\n");
                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", "payroll_exceptions.csv");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Exceptions report exported successfully!');
                  }}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  Export Report
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pay Group Modal */}
      {showEditModal && selectedPayGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Pay Group</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPayGroup(null);
                    setEditFormData({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                handleUpdatePayGroup();
              }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pay Group Name</label>
                    <input
                      type="text"
                      value={editFormData.payGroup || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 100);
                        setEditFormData(prev => ({ ...prev, payGroup: value }));
                      }}
                      onKeyPress={handleTextKeyPress}
                      maxLength={100}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {(editFormData.payGroup || '').length}/100 characters
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                    <input
                      type="text"
                      value={editFormData.period || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 50);
                        setEditFormData(prev => ({ ...prev, period: value }));
                      }}
                      onKeyPress={handleTextKeyPress}
                      maxLength={50}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {(editFormData.period || '').length}/50 characters
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editFormData.status || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="draft">Draft</option>
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="locked">Locked</option>
                      <option value="error">Error</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Headcount</label>
                    <input
                      type="number"
                      value={editFormData.headcount || ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value && parseInt(value) < 0) value = '0';
                        if (value && parseInt(value) > 100000) value = '100000';
                        setEditFormData(prev => ({ ...prev, headcount: parseInt(value) || 0 }));
                      }}
                      onKeyDown={handleNumberKeyDown}
                      min="0"
                      max="100000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {editFormData.headcount || 0}/100,000 (max)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay (₹)</label>
                    <input
                      type="number"
                      value={editFormData.netPay || ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value && parseInt(value) < 0) value = '0';
                        if (value && parseInt(value) > 1000000000) value = '1000000000';
                        setEditFormData(prev => ({ ...prev, netPay: parseInt(value) || 0 }));
                      }}
                      onKeyDown={handleNumberKeyDown}
                      min="0"
                      max="1000000000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {editFormData.netPay || 0}/1,000,000,000 (max)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Run Date</label>
                    <input
                      type="date"
                      value={editFormData.runDate || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, runDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editFormData.description || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                      setEditFormData(prev => ({ ...prev, description: value }));
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter pay group description..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {(editFormData.description || '').length}/500 characters
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedPayGroup(null);
                      setEditFormData({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Pay Group'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View All Activities Modal */}
      {showActivitiesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">All Payroll Activities</h2>
                <button
                  onClick={handleCloseModals}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        {activity.status === 'success' ? (
                          <CheckCircle className="h-6 w-6 text-green-500" />
                        ) : activity.status === 'info' ? (
                          <AlertCircle className="h-6 w-6 text-blue-500" />
                        ) : (
                          <Clock className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-gray-900">{activity.action}</h3>
                          <span className="text-sm text-gray-500">
                            {new Date(activity.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                        <p className="text-sm text-gray-500 mt-1">{activity.details}</p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-sm text-gray-500">
                            <strong>Period:</strong> {activity.period}
                          </span>
                          <span className="text-sm text-gray-500">
                            <strong>User:</strong> {activity.user}
                          </span>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${activity.status === 'success' ? 'bg-green-100 text-green-800' :
                            activity.status === 'info' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                            {activity.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <Button
                  variant="secondary"
                  onClick={handleCloseModals}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  icon={<Download className="h-4 w-4" />}
                  onClick={() => {
                    const csvContent = "data:text/csv;charset=utf-8,"
                      + "ID,Action,Description,Period,User,Status,Timestamp\n"
                      + recentActivities.map(activity =>
                        `${activity.id},"${activity.action}","${activity.description}",${activity.period},${activity.user},${activity.status},${activity.timestamp}`
                      ).join("\n");
                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", "payroll_activities.csv");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Activities report exported successfully!');
                  }}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  Export Report
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exception Detail Modal */}
      {showExceptionModal && selectedException && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Exception Details</h2>
                <button
                  onClick={() => {
                    setShowExceptionModal(false);
                    setSelectedException(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Employee Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Employee Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Employee Name</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.employeeName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Employee ID</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.employeeId}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Department</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.department}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Designation</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.designation}</p>
                    </div>
                  </div>
                </div>

                {/* Exception Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Exception Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Exception Type</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.type}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.description}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Severity</label>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(selectedException.severity)}`}>
                          {selectedException.severity}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedException.status)}`}>
                          {selectedException.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created Date</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedException.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                {/* Resolution Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Resolution Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Resolution Notes</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.resolutionNotes || 'No resolution notes available'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Resolved By</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.resolvedBy || 'Not resolved yet'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Resolved Date</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedException.resolvedAt ? new Date(selectedException.resolvedAt).toLocaleDateString() : 'Not resolved yet'}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowExceptionModal(false);
                      setSelectedException(null);
                    }}
                  >
                    Close
                  </Button>
                  {selectedException.status === 'open' && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        toast.success('Exception marked as resolved');
                        setShowExceptionModal(false);
                        setSelectedException(null);
                      }}
                    >
                      Mark as Resolved
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PayrollDashboard;